/**
 * Twin correlations resource — typed wrapper over `/v1/twin/correlations`
 * (B5.4).
 *
 * The server returns a precomputed analysis document (real Pearson over
 * the seeded heats) shaped to match the UI's FX_CORRELATION_* constants,
 * so the explorer destructures it directly.
 */

import { api } from "@/lib/api-client";
import type { CorrelationCell } from "@foundry/CorrelationHeatmap";
import type {
  CorrelationInsight,
  ScatterSample,
} from "@/lib/widgets/__foundry_fixtures";
import { qs } from "./qs";

/** One cell of the defects×params matrix: Pearson r between a defect's per-heat
 *  rate and a process parameter's value. */
export interface DefectCorrelationCell {
  defect: string;
  param: string;
  r: number;
  pValue?: number;
  /** Contributing heat ROWS. */
  n?: number;
  /** Distinct measured values behind those rows. A lab value carried across many
   *  heats gives nDistinct << n: the rows are copies, not observations. */
  nDistinct?: number;
  /** The N the p-value was computed from (the independent-measurement count). */
  nEff?: number;
  /** True when nDistinct < n. */
  carryForward?: boolean;
}

/** One cell of a grade/shift × defect association matrix. `r` is the unit-level
 *  lift squashed to [-1,1] (positive = over-represented vs the plant base rate). */
export interface CategoricalCell {
  row: string;
  col: string;
  r: number;
  pValue?: number;
  n?: number;
}

/** A grade/shift × defect association matrix (heat-level). */
export interface CategoricalMatrix {
  dimension: "grade" | "shift" | string;
  rows: string[];
  rowLabels?: Record<string, string>;
  cols: string[];
  colLabels?: Record<string, string>;
  cells: CategoricalCell[];
}

export interface CorrelationAnalysis {
  params: string[];
  labels: Record<string, string>;
  cells: CorrelationCell[];
  scatter: Record<string, ScatterSample[]>;
  insights: CorrelationInsight[];
  n?: number;
  // Defects×process-parameters matrix (live-computed from tracked_units).
  // Optional — absent for orgs/heats with no recorded defects. `defectAxes` is
  // the DYNAMIC x-axis: only defect types actually present in the heats.
  defectAxes?: string[];
  defectLabels?: Record<string, string>;
  defectCells?: DefectCorrelationCell[];
  /** Per defect×param scatter (param value x, defect rate y), keyed
   *  `${defect}|${param}` — backs the defect cell-detail sheet. */
  defectScatter?: Record<string, ScatterSample[]>;
  /** param → a representative bound tag (entity_tag_bindings.port == param), so
   *  the correlation "create alarm" deep-link can pre-select the real target tag. */
  paramTags?: Record<string, { tag_id: string; tag_name: string }>;
  /** Echoes the requested observation unit / coefficient. `lot` = the produced
   *  casting lot (D1) — params inherited from its heats, defects its own. */
  granularity?: "heat" | "lot" | "day" | "week" | "month";
  method?: "pearson" | "spearman";
  /** Defect co-occurrence (defect×defect phi) — reuses the CorrelationCell shape
   *  (symmetric, strictly-upper; diagonal self=1 implied). */
  cooccurAxes?: string[];
  cooccurLabels?: Record<string, string>;
  cooccurCells?: CorrelationCell[];
  /** Grade/shift × defect association matrices (heat-level). */
  categorical?: CategoricalMatrix[];
  /** True when the org's heats are source='generated' (a demo/simulated plant) —
   *  the matrix reflects PLANTED patterns, not relationships discovered in real
   *  production. Set by the server (C4). The UI badges the whole page when true so
   *  a demo correlation is never mistaken for a finding. */
  simulated?: boolean;
}

/** Query params for the correlation analysis. `granularity`/`method` shape the
 *  two continuous matrices; `from`/`to` window the heats the whole analysis
 *  covers (so a windowed report's matrix matches its date range). */
export type CorrelationParams = {
  org_id?: string;
  granularity?: "heat" | "lot" | "day" | "week" | "month";
  method?: "pearson" | "spearman";
  from?: string;
  to?: string;
};

export const twinCorrelations = {
  get: (params?: CorrelationParams) =>
    api.get<CorrelationAnalysis>(`/v1/twin/correlations${qs(params)}`),
};
